import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Headset, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [convId, setConvId] = useState(() => localStorage.getItem("stx_chat_id") || "");
    const [nome, setNome] = useState(() => localStorage.getItem("stx_chat_nome") || "");
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [online, setOnline] = useState(false);
    const [showFab, setShowFab] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        const check = () => setShowFab(window.scrollY > 320 || window.innerWidth >= 640);
        check();
        window.addEventListener("scroll", check, { passive: true });
        window.addEventListener("resize", check);
        return () => {
            window.removeEventListener("scroll", check);
            window.removeEventListener("resize", check);
        };
    }, []);

    useEffect(() => {
        const ping = async () => {
            try {
                const r = await axios.get(`${API}/chat/presence`);
                setOnline(r.data.online);
            } catch {}
        };
        ping();
        const t = setInterval(ping, 15000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (!open || !convId) return;
        const fetchMsgs = async () => {
            try {
                const r = await axios.get(`${API}/chat/${convId}/messages`);
                setMessages(r.data);
            } catch {}
        };
        fetchMsgs();
        const t = setInterval(fetchMsgs, 4000);
        return () => clearInterval(t);
    }, [open, convId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const start = async () => {
        if (!nome.trim()) return;
        try {
            const r = await axios.post(`${API}/chat/start`, { nome: nome.trim() });
            localStorage.setItem("stx_chat_id", r.data.conversation_id);
            localStorage.setItem("stx_chat_nome", nome.trim());
            setConvId(r.data.conversation_id);
        } catch {}
    };

    const send = async () => {
        const value = text.trim();
        if (!value || !convId) return;
        setText("");
        try {
            await axios.post(`${API}/chat/${convId}/messages`, { text: value });
            const r = await axios.get(`${API}/chat/${convId}/messages`);
            setMessages(r.data);
        } catch {}
    };

    return (
        <>
            <AnimatePresence>
                {showFab && (
                    <motion.button
                        data-testid="chat-widget-button"
                        onClick={() => setOpen(!open)}
                        aria-label="Abrir chat online"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="group fixed bottom-24 right-4 z-50 flex items-center gap-0 rounded-full border border-cyan-400/40 bg-[#0D121F] p-4 text-cyan-300 shadow-[0_10px_36px_rgba(0,136,255,0.3)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-cyan-300 sm:right-auto sm:left-6"
                    >
                        {open ? <X size={24} /> : <Headset size={24} />}
                        {!open && (
                            <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-bold transition-all duration-500 group-hover:ml-2.5 group-hover:max-w-[140px]">
                                Chat online
                            </span>
                        )}
                    </motion.button>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="chat-panel"
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.96 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed bottom-40 left-4 z-50 flex h-[460px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0D121F] shadow-[0_24px_70px_rgba(0,0,0,0.6)] sm:left-6"
                    >
                        <div className="border-b border-white/[0.07] bg-[#111827] px-5 py-4">
                            <p className="text-sm font-extrabold text-slate-50">Fale com a Strondex Tech</p>
                            <p data-testid="chat-online-status" className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                                <span className={`h-2 w-2 rounded-full ${online ? "beacon-dot bg-[#25D366]" : "bg-slate-500"}`} />
                                {online ? "Online agora — resposta imediata" : "Deixe sua dúvida, respondemos rapidinho"}
                            </p>
                        </div>

                        {!convId ? (
                            <div className="flex flex-1 flex-col justify-center gap-4 p-6">
                                <p className="text-sm leading-relaxed text-slate-400">
                                    Tire suas dúvidas direto com a gente. Como podemos te
                                    chamar?
                                </p>
                                <input
                                    data-testid="chat-name-input"
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && start()}
                                    placeholder="Seu nome"
                                    className="rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-cyan-400/60"
                                />
                                <button
                                    data-testid="chat-start-button"
                                    onClick={start}
                                    disabled={!nome.trim()}
                                    className="rounded-full bg-cyan-500 px-5 py-3 text-sm font-bold text-[#03202e] transition-all duration-300 hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    Iniciar conversa
                                </button>
                            </div>
                        ) : (
                            <>
                                <div data-testid="chat-messages" className="flex-1 space-y-2.5 overflow-y-auto p-4">
                                    {messages.length === 0 && (
                                        <p className="pt-16 text-center text-xs text-slate-600">
                                            Envie sua primeira mensagem abaixo
                                        </p>
                                    )}
                                    {messages.map((m) => (
                                        <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                                            <div
                                                className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                                                    m.sender === "visitor"
                                                        ? "rounded-br-md bg-cyan-500 text-[#03202e]"
                                                        : "rounded-bl-md border border-white/[0.07] bg-white/[0.06] text-slate-200"
                                                }`}
                                            >
                                                <p>{m.text}</p>
                                                <p className={`mt-1 text-right text-[10px] ${m.sender === "visitor" ? "text-[#03202e]/60" : "text-slate-600"}`}>
                                                    {fmtTime(m.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={bottomRef} />
                                </div>
                                <div className="flex items-center gap-2 border-t border-white/[0.07] p-3">
                                    <input
                                        data-testid="chat-message-input"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && send()}
                                        placeholder="Digite sua mensagem..."
                                        className="flex-1 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-cyan-400/60"
                                    />
                                    <button
                                        data-testid="chat-send-button"
                                        onClick={send}
                                        disabled={!text.trim()}
                                        aria-label="Enviar mensagem"
                                        className="rounded-full bg-cyan-500 p-2.5 text-[#03202e] transition-all duration-300 hover:bg-cyan-400 disabled:opacity-40"
                                    >
                                        <Send size={17} />
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
